import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { api, imgSrc, pickLang } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const LOCALES = { it: "it-IT", en: "en-GB", es: "es-ES" };

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/posts")
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="pt-36 sm:pt-40 pb-24">
      <Seo
        title={t("seo.blogTitle")}
        siteName="ROMACASA"
        description={t("seo.blogDesc")}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-14">
          <Eyebrow>{t("blog.eye")}</Eyebrow>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mt-4 mb-6">{t("blog.title")}</h1>
          <p className="text-[#5B6670] font-light text-base sm:text-lg leading-relaxed">{t("blog.sub")}</p>
        </Reveal>

        {loading ? (
          <div className="py-24 text-center text-[#5B6670] font-light" data-testid="blog-loading">{t("blog.loading")}</div>
        ) : posts.length === 0 ? (
          <div className="py-24 text-center" data-testid="blog-empty">
            <p className="font-serif-d text-3xl text-black">{t("blog.empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
                <Link
                  to={`/blog/${p.id}`}
                  data-testid={`post-card-${p.id}`}
                  className="group block bg-white border border-[#E0E3E6] overflow-hidden hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.18)] transition-shadow duration-500 h-full"
                >
                  {p.cover && (
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img src={imgSrc(p.cover)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.2s] ease-out" />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="font-mono-d text-xs text-[#8A94A0] mb-3">
                      {new Date(p.created_at).toLocaleDateString(LOCALES[lang] || "it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                    <h2 className="font-serif-d text-2xl font-medium leading-snug group-hover:text-[#2E2E2E] transition-colors">{pickLang(p, "title", lang)}</h2>
                    {pickLang(p, "excerpt", lang) && <p className="mt-3 text-sm text-[#5B6670] font-light leading-relaxed line-clamp-3">{pickLang(p, "excerpt", lang)}</p>}
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-black group-hover:gap-3.5 transition-all">
                      {t("blog.leggi")} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
